import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Table from '@/components/Tables/Table/Table';
import { FranchiseeBundleAddOnRow } from '@/types/namedPrice.types';
import { ColumnDef } from '@tanstack/react-table';
import styles from './StoreGroupPricesTable.module.css';

export interface StoreGroupWithCount {
  storeGroup: string;
  selected: number;
  total: number;
}

interface StoreGroupPricesTableProps {
  addOn: FranchiseeBundleAddOnRow;
  storeGroups: StoreGroupWithCount[];
  onPriceDeviationsClick?: (namedPriceId: string, selectedGroupName: string) => void;
}

interface PriceCellData {
  display: string;
  isRange: boolean;
}

interface AddOnPriceRow {
  bundleAddOn: string;
  [storeGroupName: string]: string | PriceCellData;
}

const StoreGroupPricesTable: FC<StoreGroupPricesTableProps> = ({ addOn, storeGroups, onPriceDeviationsClick }) => {
  const { t } = useTranslation();

  const tableData: AddOnPriceRow[] = useMemo(() => {
    const row: AddOnPriceRow = { bundleAddOn: addOn.name };
    storeGroups.forEach(({ storeGroup }) => {
      const sg = addOn.storeGroupPrices?.[storeGroup];
      if (sg?.priceDisplay) {
        const displayPrice = sg.priceDisplay.startsWith('$') ? sg.priceDisplay : `$${sg.priceDisplay}`;
        row[storeGroup] = {
          display: displayPrice,
          isRange: sg.isRange || false,
        };
      } else {
        row[storeGroup] = '—';
      }
    });
    return [row];
  }, [addOn, storeGroups]);

  const columns: ColumnDef<AddOnPriceRow, unknown>[] = useMemo(() => {
    const cols: ColumnDef<AddOnPriceRow, unknown>[] = [
      {
        id: 'bundleAddOn',
        accessorKey: 'bundleAddOn',
        header: t('franchiseeBundleAddOns.bundleAddOnColumn'),
        cell: (info) => <div className={styles.addOnName}>{info.getValue() as string}</div>,
        size: 350,
        minSize: 250,
        maxSize: 450,
      },
    ];

    storeGroups.forEach(({ storeGroup, selected, total }, index) => {
      cols.push({
        id: `sg_${storeGroup}_${index}`,
        accessorKey: storeGroup,
        header: () => (
          <div className={styles.headerWithCount}>
            <div className={styles.headerName}>{storeGroup.toUpperCase()}</div>
            <div className={styles.headerCount}>
              {selected} / {total}
            </div>
          </div>
        ),
        size: 145,
        minSize: 130,
        maxSize: 160,
        cell: (info) => {
          const cellData = info.getValue();

          // If it's a string (e.g., "—"), display it directly
          if (typeof cellData === 'string') {
            return <span>{cellData}</span>;
          }

          // If it's an object with display and isRange
          if (cellData && typeof cellData === 'object' && 'display' in cellData) {
            const { display, isRange } = cellData as PriceCellData;

            if (isRange) {
              return (
                <div className={styles.priceWithIcon}>
                  <span>{display}</span>
                  <InfoOutlinedIcon
                    className={styles.infoIcon}
                    role="button"
                    aria-label="Price deviations"
                    sx={{
                      width: '18px',
                      height: '18px',
                      color: '#266DCC',
                      cursor: onPriceDeviationsClick ? 'pointer' : 'default',
                      marginLeft: '4px',
                    }}
                    onClick={() => onPriceDeviationsClick?.(addOn.namedPriceId, storeGroup)}
                  />
                </div>
              );
            }

            return <span>{display}</span>;
          }

          return <span>—</span>;
        },
      });
    });

    return cols;
  }, [storeGroups, t, addOn.namedPriceId, onPriceDeviationsClick]);

  return (
    <div className={styles.container}>
      <div className={styles.tableWrapper}>
        <Table
          data={tableData}
          columns={columns}
          height="130px"
          noRowsMessage={null}
          className={styles.tableNoScroll}
        />
      </div>
    </div>
  );
};

export default StoreGroupPricesTable;
